import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RouteAnalytics } from "@/components/telemetry/RouteAnalytics";
import { DocumentTitle } from "@/components/layout/DocumentTitle";
import { PostLoginRedirect } from "@/components/auth/PostLoginRedirect";
import { routeMinRole } from "@/lib/app-nav";
import { queryClient } from "@/lib/queryClient";
import { AppLayout } from "@/components/layout/AppLayout";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { GuestGuard } from "@/components/auth/GuestGuard";
import { ProjectRouteGuard } from "@/components/auth/ProjectRouteGuard";
import Login from "@/pages/Login";
import AdtConnected from "@/pages/AdtConnected";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import ProjectRuns from "@/pages/project/ProjectRuns";
import ProjectArtifacts from "@/pages/project/ProjectArtifacts";
import ProjectConnections from "@/pages/project/ProjectConnections";
import ProjectCost from "@/pages/project/ProjectCost";
import ProjectSettings from "@/pages/project/ProjectSettings";
import ProjectCalibration from "@/pages/project/ProjectCalibration";
import ProjectCommercialCalibration from "@/pages/project/ProjectCommercialCalibration";
import ProjectDiagnostic from "@/pages/project/ProjectDiagnostic";
import ClientPdfPage from "@/pages/project/ClientPdfPage";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminIntegration from "@/pages/admin/AdminIntegration";
import AdminReferenceData from "@/pages/admin/AdminReferenceData";
import AdminReferenceDataSource from "@/pages/admin/AdminReferenceDataSource";
import AdminReferenceDataConfigType from "@/pages/admin/AdminReferenceDataConfigType";
import AdminReferenceDataAgent from "@/pages/admin/AdminReferenceDataAgent";
import Profile from "@/pages/Profile";
import Architect from "@/pages/agents/Architect";
import { ConfigurationServerLayout } from "@/components/agents/configuration-server/stages/ConfigurationServerLayout";
import ConfigurationServer from "@/pages/agents/ConfigurationServer";
import ManagementStage from "@/pages/agents/config-server/ManagementStage";
import ExtractionStage from "@/pages/agents/config-server/ExtractionStage";
import ExecutionStage from "@/pages/agents/config-server/ExecutionStage";
import CodeCompanion from "@/pages/project/CodeCompanion";
import FunctionalWorkbench from "@/pages/agents/FunctionalWorkbench";
import Index from "@/pages/Index";
import NotFound from "./pages/NotFound";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Rotas antigas que só sobrevivem como redirect para /projects — favoritos e
// links salvos (e-mail, Slack) continuam funcionando em vez de caírem em 404.
// A série SIMP (redução de superfície do produto, epic #422) foi adicionando
// uma rota por vez: /dashboard (SIMP.2 #423), /agents e /artifacts (SIMP.3
// #424). Uma tabela em vez de um <Route> por decisão mantém o conjunto num
// lugar só e greppável — é o que interessa quando alguém perguntar "quais
// rotas antigas ainda redirecionam".
const LEGACY_REDIRECTS = ["/dashboard", "/agents", "/artifacts"];

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <RouteAnalytics />
          <DocumentTitle />
          <PostLoginRedirect />
          <SentryRoutes>
            <Route path="/" element={<GuestGuard><Index /></GuestGuard>} />
            <Route path="/login" element={<GuestGuard><Login /></GuestGuard>} />
            {/* Retorno do login ADT (S-070). Fora do AuthGuard de proposito: o ticket
                tem uso unico e vida curta, e um redirect para o login o perderia. */}
            <Route path="/adt/connected" element={<AdtConnected />} />
            {/* O wizard lê a sessão da store, e quem a povoa é o `loadUser()` dos
                guards — sem um deles aqui, `isAuthenticated` ficava `false` para
                sempre e o passo 1 travava. Fora do `AppLayout` de propósito: é
                tela de setup, sem sidebar. */}
            {LEGACY_REDIRECTS.map((path) => (
              <Route key={path} path={path} element={<Navigate to="/projects" replace />} />
            ))}
            <Route path="/projects/:projectId/agents/functional" element={
              <AuthGuard>
                <ProjectRouteGuard slug="agents/functional"><FunctionalWorkbench /></ProjectRouteGuard>
              </AuthGuard>
            } />
            <Route
              element={
                <AuthGuard>
                  <AppLayout />
                </AuthGuard>
              }
            >
              <Route path="/profile" element={<Profile />} />
              {/* As quatro rotas com papel mínimo. O `AuthGuard` externo já cuidou
                  da autenticação; este segundo, aninhado, só checa o papel — e o
                  mínimo vem do catálogo (`lib/app-nav.ts`), que é a mesma lista que
                  a sidebar filtra. Escrevê-lo aqui à mão foi o que deixou link e
                  rota divergirem do backend em silêncio. */}
              <Route path="/admin/team" element={<AuthGuard minRole={routeMinRole("/admin/team")}><AdminTeam /></AuthGuard>} />
              <Route path="/admin/settings" element={<AuthGuard minRole={routeMinRole("/admin/settings")}><AdminSettings /></AuthGuard>} />
              <Route path="/admin/integration" element={<AuthGuard minRole={routeMinRole("/admin/integration")}><AdminIntegration /></AuthGuard>} />
              <Route path="/admin/reference-data" element={<AuthGuard minRole={routeMinRole("/admin/reference-data")}><AdminReferenceData /></AuthGuard>} />
              <Route path="/admin/reference-data/:tableName" element={<AuthGuard minRole={routeMinRole("/admin/reference-data/:tableName")}><AdminReferenceDataSource /></AuthGuard>} />
              <Route path="/admin/reference-data/agents/:agentKey" element={<AuthGuard minRole={routeMinRole("/admin/reference-data/agents/:agentKey")}><AdminReferenceDataAgent /></AuthGuard>} />
              <Route path="/admin/reference-data/agents/:agentKey/:typeKey" element={<AuthGuard minRole={routeMinRole("/admin/reference-data/agents/:agentKey/:typeKey")}><AdminReferenceDataConfigType /></AuthGuard>} />
              <Route path="/projects" element={<Projects />} />
              {/* Toda tela de projeto passa pelo guarda. O papel mínimo de cada
                  uma mora no catálogo (`lib/project-nav.ts`), que é a mesma lista
                  que a sidebar filtra — repetir o mínimo aqui faria link e URL
                  divergirem em silêncio. Um `user` só alcança o Diagnóstico, e
                  abrir o projeto o leva direto para lá.

                  As sete abas da sidebar: */}
              <Route path="/projects/:projectId" element={<ProjectRouteGuard slug=""><ProjectDetail /></ProjectRouteGuard>} />
              <Route path="/projects/:projectId/runs" element={<ProjectRouteGuard slug="runs"><ProjectRuns /></ProjectRouteGuard>} />
              <Route path="/projects/:projectId/artifacts" element={<ProjectRouteGuard slug="artifacts"><ProjectArtifacts /></ProjectRouteGuard>} />
              <Route path="/projects/:projectId/connections" element={<ProjectRouteGuard slug="connections"><ProjectConnections /></ProjectRouteGuard>} />
              <Route path="/projects/:projectId/cost" element={<ProjectRouteGuard slug="cost"><ProjectCost /></ProjectRouteGuard>} />
              <Route path="/projects/:projectId/settings" element={<ProjectRouteGuard slug="settings"><ProjectSettings /></ProjectRouteGuard>} />
              <Route path="/projects/:projectId/calibration" element={<ProjectRouteGuard slug="calibration"><ProjectCalibration /></ProjectRouteGuard>} />
              <Route path="/projects/:projectId/commercial-calibration" element={<ProjectRouteGuard slug="commercial-calibration"><ProjectCommercialCalibration /></ProjectRouteGuard>} />
              <Route path="/projects/:projectId/diagnostic/pdf" element={<ProjectRouteGuard slug="diagnostic/pdf"><ClientPdfPage /></ProjectRouteGuard>} />
              <Route path="/projects/:projectId/diagnostic/*" element={<ProjectRouteGuard slug="diagnostic"><ProjectDiagnostic /></ProjectRouteGuard>} />
              {/* E os workbenches, que não são abas: chega-se neles pela Overview,
                  mas cada um tem URL própria e abria com um link colado. */}
              <Route path="/projects/:projectId/agents/architect" element={<ProjectRouteGuard slug="agents/architect"><Architect /></ProjectRouteGuard>} />
              <Route element={<ConfigurationServerLayout />}>
              <Route path="/projects/:projectId/agents/configuration-server" element={<ProjectRouteGuard slug="agents/configuration-server"><ConfigurationServer /></ProjectRouteGuard>} />
              <Route path="/projects/:projectId/agents/configuration-server/extraction" element={<ProjectRouteGuard slug="agents/configuration-server/extraction"><ExtractionStage /></ProjectRouteGuard>} />
              <Route path="/projects/:projectId/agents/configuration-server/management" element={<ProjectRouteGuard slug="agents/configuration-server/management"><ManagementStage /></ProjectRouteGuard>} />
              <Route path="/projects/:projectId/agents/configuration-server/execution" element={<ProjectRouteGuard slug="agents/configuration-server/execution"><ExecutionStage /></ProjectRouteGuard>} />
              </Route>
              <Route path="/projects/:projectId/agents/code-companion" element={<ProjectRouteGuard slug="agents/code-companion"><CodeCompanion /></ProjectRouteGuard>} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </SentryRoutes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
